import React, {useState, useCallback} from 'react';
import type {FC, ReactNode} from 'react';

import type {ButtonView} from '@gravity-ui/uikit';
import {Dialog} from '@gravity-ui/uikit';

import {cnConfirmDialog} from './ConfirmDialog.classname';

import './ConfirmDialog.scss';

export interface ConfirmDialogData {
    title?: string;
    message?: ReactNode;
    confirmation?: ReactNode;
    actionId?: string;
    actionText: string;
    actionTheme?: ActionTheme;
    cancelTheme?: ActionTheme;
    actionHandler: () => void | Promise<unknown>;
    subActionHandler?: () => void | Promise<unknown>;
    cancelText: string;
}

export interface ConfirmDialogProps {
    open: boolean;
    disabled?: boolean;
    data: ConfirmDialogData;
    onRequestClose: () => void;
    onSuccess?: (actionId?: string) => void;
    onError?: (err: Error, actionId?: string) => void;
    onCancel?: () => void;
    className?: string;
    size?: 's' | 'm' | 'l';
}

export enum ActionTheme {
    Normal = 'normal',
    Action = 'action',
    Danger = 'danger',
}

const actionButtonViewByTheme: Record<ActionTheme, ButtonView> = {
    [ActionTheme.Normal]: 'normal',
    [ActionTheme.Action]: 'action',
    [ActionTheme.Danger]: 'outlined-danger',
};

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
    size = 's',
    open = false,
    disabled,
    data = {},
    onCancel,
    onError,
    onRequestClose,
    onSuccess,
}) => {
    const {
        title,
        message,
        actionText,
        actionTheme,
        cancelTheme,
        cancelText,
        actionId,
        actionHandler,
        subActionHandler,
    } = data;

    const [isLoading, setIsLoading] = useState(false);

    const handleCancelClick = useCallback(() => {
        try {
            onRequestClose();
            onCancel?.();
        } catch (error) {
            onError?.(error as Error, actionId);

            throw error;
        }
    }, [actionId, onRequestClose, onCancel, onError]);

    const handleActionClick = useCallback(() => {
        if (actionHandler) {
            setIsLoading(true);

            Promise.resolve(actionHandler())
                .then(() => {
                    setIsLoading(false);

                    onRequestClose();

                    if (onSuccess) {
                        onSuccess(actionId);
                    }
                })
                .catch((error) => {
                    setIsLoading(false);

                    if (onError) {
                        onError(error, actionId);
                    }

                    throw error;
                });
        }
    }, [actionId, actionHandler, onSuccess, onRequestClose, onError]);

    const handleSubActionClick = useCallback(() => {
        if (subActionHandler) {
            setIsLoading(true);

            Promise.resolve(subActionHandler())
                .then(() => {
                    setIsLoading(false);

                    onRequestClose();

                    if (onSuccess) {
                        onSuccess(actionId);
                    }
                })
                .catch((error) => {
                    setIsLoading(false);

                    if (onError) {
                        onError(error, actionId);
                    }

                    throw error;
                });
        } else {
            handleCancelClick();
        }
    }, [actionId, subActionHandler, onSuccess, onRequestClose, onError, handleCancelClick]);

    return (
        <Dialog open={open} onClose={handleCancelClick} size={size} className={cnConfirmDialog()}>
            <Dialog.Header caption={title} />
            <Dialog.Body>
                {message && <div className={cnConfirmDialog('Message')}>{message}</div>}
            </Dialog.Body>
            <Dialog.Footer
                preset="default"
                textButtonCancel={cancelText}
                textButtonApply={actionText}
                onClickButtonCancel={handleSubActionClick}
                onClickButtonApply={handleActionClick}
                propsButtonApply={{
                    disabled,
                    view: actionTheme ? actionButtonViewByTheme[actionTheme] : 'action',
                }}
                propsButtonCancel={{
                    disabled: subActionHandler ? disabled : false,
                    view: cancelTheme ? actionButtonViewByTheme[cancelTheme] : 'flat',
                }}
                loading={isLoading}
            />
        </Dialog>
    );
};
